import {
  createMutationKeys,
  createQueryKeys,
  mergeQueryKeys,
} from '@lukemorales/query-key-factory'
import { RequestTypes } from '@core/react-query/features/templates/permitTemplates/requestTypes'
import { APIAxiosInstance } from '@core/utils/api-axios-instance'
import { createQueryKeyString } from '@core/react-query/queryParams'
import { IPermitTemplate } from '@core/entities/permit/PermitTemplate'
import { PageResponse } from '@core/entities/pagination'

const permitTemplateQueryKeys = createQueryKeys('permit-templates', {
  byOrganisation: ({
    organisationId,
    projectIds,
    page,
    size,
    excludeProjectId,
    term,
    status,
  }: RequestTypes['getOrganisationTemplates']) => {
    return {
      queryKey: [organisationId, projectIds, excludeProjectId, term],
      queryFn: async ({ pageParam = 1 }) => {
        const queryString = createQueryKeyString({
          organisationId,
          projectIds,
          page: page ? page : pageParam,
          size,
          excludeProjectId,
          term,
          status,
        })
        const { data } = await APIAxiosInstance.get(
          `permits/templates${queryString}`,
        )
        return data as PageResponse<IPermitTemplate>
      },
    }
  },
  filterList: ({
    organisationId,
    projectIds,
  }: RequestTypes['getPermitTemplateFilterList']) => {
    return {
      queryKey: [organisationId, projectIds],
      queryFn: async ({}) => {
        const queryString = createQueryKeyString({
          organisationId,
          projectIds,
        })
        const { data } = await APIAxiosInstance.get(
          `permits/templates/filter-list${queryString}`,
        )
        return data as IPermitTemplate[]
      },
    }
  },
  page: ({
    organisationId,
    projectIds,
    page,
    size,
    excludeProjectId,
    term,
  }: RequestTypes['getOrganisationTemplates']) => {
    return {
      queryKey: [organisationId, projectIds, excludeProjectId, term],
      queryFn: async () => {
        const queryString = createQueryKeyString({
          organisationId,
          projectIds,
          page,
          size,
          excludeProjectId,
          term,
        })
        const { data } = await APIAxiosInstance.get(
          `permits/templates${queryString}`,
        )
        return data as PageResponse<IPermitTemplate>
      },
    }
  },
  availableTemplates: ({
    projectId,
    registerItemId,
    parentTemplateId,
  }: RequestTypes['getAvailableTemplates']) => {
    return {
      queryKey: ['v2', projectId, registerItemId, parentTemplateId],
      queryFn: async () => {
        const queryString = createQueryKeyString({
          projectId,
          registerItemId,
          parentTemplateId,
        })
        const { data } = await APIAxiosInstance.get(
          `permits/templates/create-list${queryString}`,
        )
        return data
      },
    }
  },
  getRoles: (payload: RequestTypes['getPermitTemplateRoles']) => {
    return {
      queryKey: [payload.templateVersionId],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `permits/templates/${payload.templateId}/versions/${payload.templateVersionId}/roles`,
        )

        return data.roles
      },
    }
  },
  getRolesByProject: (
    payload: RequestTypes['getPermitTemplateRolesByProject'],
  ) => {
    return {
      queryKey: ['byProject', payload.templateId, payload.projectId],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `permits/templates/${payload.templateId}/roles?projectId=${payload.projectId}`,
        )

        return data.roles
      },
    }
  },
  getPermitTemplatesReport: ({
    reportingType,
    organisationId,
  }: RequestTypes['getPermitTemplatesReport']) => {
    return {
      queryKey: [reportingType, organisationId],
      queryFn: async () => {
        const queryString = createQueryKeyString({
          organisationId,
        })
        const { data } = await APIAxiosInstance.get(
          `reporting/templates/permits/${reportingType}${queryString}`,
        )

        return data
      },
    }
  },
})

const permitTemplateMutationsKeys = createMutationKeys('permit-templates', {
  createPermitRole: {
    mutationKey: null,
    mutationFn: async ({
      templateId,
      templateVersionId,
      ...rest
    }: RequestTypes['createPermitRole']) => {
      const { data } = await APIAxiosInstance.post(
        `permits/templates/${templateId}/versions/${templateVersionId}/roles`,
        rest,
      )
      return data
    },
  },
  updatePermitRole: {
    mutationKey: null,
    mutationFn: async ({
      templateId,
      templateVersionId,
      id,
      ...rest
    }: RequestTypes['updatePermitRole']) => {
      const { data } = await APIAxiosInstance.put(
        `permits/templates/${templateId}/versions/${templateVersionId}/roles/${id}`,
        rest,
      )
      return data
    },
  },
  deletePermitRole: {
    mutationKey: null,
    mutationFn: async ({
      templateId,
      templateVersionId,
      id,
    }: RequestTypes['deletePermitRole']) => {
      const { data } = await APIAxiosInstance.delete(
        `permits/templates/${templateId}/versions/${templateVersionId}/roles/${id}`,
      )
      return data
    },
  },
})

export const permitTemplatesKeys = mergeQueryKeys(
  permitTemplateQueryKeys,
  permitTemplateMutationsKeys,
)['permit-templates']
